import { useMemo } from 'react';

import orderBy from 'lodash/orderBy';

import { OrganizationRole } from '@/graphql';
import Fuse from 'fuse.js';

import { OrgUserFragment } from './__generated__/fragment.generated';
import { useOrgUsersQuery } from './__generated__/queries.generated';

function useOrgUsers() {
  const { data, loading } = useOrgUsersQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    users: data?.users || [],
    loading,
  };
}

const FUSE_SEARCH_OPTIONS: Fuse.IFuseOptions<OrgUserFragment> = {
  isCaseSensitive: false,
  includeScore: false,
  shouldSort: true,
  includeMatches: false,
  findAllMatches: false,
  minMatchCharLength: 1,
  location: 0,
  threshold: 0,
  distance: 100,
  useExtendedSearch: false,
  ignoreLocation: false,
  ignoreFieldNorm: false,
  keys: [
    {
      name: 'name',
      weight: 1,
    },
    {
      name: 'email',
      weight: 1,
    },
  ],
};

function useFilterOrgUsers(query: string, useOnlyOrgMembers: boolean = false) {
  const { users, loading } = useOrgUsers();

  const usersToInclude = useMemo(() => {
    if (useOnlyOrgMembers) {
      return users.filter((user) => user.roleName !== OrganizationRole.Admin);
    }

    return users;
  }, [useOnlyOrgMembers, users]);

  const orderedUsers = useMemo(() => {
    return orderBy(usersToInclude, ['name'], ['asc']);
  }, [usersToInclude]);

  const filteredUsers = useMemo(() => {
    if (!query) {
      return orderedUsers;
    }

    const fuse = new Fuse<OrgUserFragment>(orderedUsers, FUSE_SEARCH_OPTIONS);
    return fuse.search(query).map((result) => result.item);
  }, [query, orderedUsers]);

  return {
    filteredUsers,
    loading,
    allUsers: usersToInclude,
  };
}

export { useOrgUsers, useFilterOrgUsers };
